
























import Vue from 'vue'
import { Component, Prop, Emit, Watch } from 'vue-property-decorator'
import { showErrors, showNotification } from '@/utils/snack'
import { SnackType } from '@/interfaces/snack'
import AppDialog from '@/components/AppDialog.vue'
import AppSteps from '@/components/AppSteps.vue'
import AccountAssetFormInfo from '@/modules/AccountAsset/components/forms/AccountAssetFormInfo.vue'
import AccountAssetFormProperty from '@/modules/AccountAsset/components/forms/AccountAssetFormProperty.vue'
import AccountAssetFormType from '@/modules/AccountAsset/components/forms/AccountAssetFormType.vue'

import { newAssetProperty, editAssetProperty } from '@/modules/AccountAsset/gql/account-asset.gql'
import { AccountAsset, AccountAssetType } from '@/modules/AccountAsset/interfaces/account-asset'
import { IAssetInfoInput, IAccountPropertyInput } from '@/modules/AccountAsset/interfaces/account-asset-form'
import { states } from '@/utils/us_states'

@Component({
  components: {
    AccountAssetFormInfo,
    AccountAssetFormProperty,
    AccountAssetFormType,
    AppDialog,
    AppSteps
  }
})
export default class AssetPropertyForm extends Vue {
  @Prop(Boolean) value!: boolean
  @Prop(Number) readonly step!: number
  @Prop(String) readonly title!: string
  @Prop({ type: Object as () => AccountAsset }) readonly accountAsset!: AccountAsset
  @Prop(Number) readonly accountId!: number

  showDialog = false

  loading = false

  stepIndex = 0

  infoInput: IAssetInfoInput = {
    name: '',
    notes: ''
  }

  propertyInput: IAccountPropertyInput = {
    address1: '',
    address2: '',
    city: '',
    stateIdx: -1,
    country: '',
    zipCode: '',
    propertyType: '',
    owner: '',
    price: '',
    propertyStatus: '',
    creationTime: '',
    buyTime: ''
  }

  get steps () {
    return [
      ['Asset Info', 770, 421],
      ['Property Info', 770, 751]
    ]
  }

  get stepItems () {
    return this.steps.map(st => st[0])
  }

  get showBackBtn () {
    return this.stepIndex > 0
  }

  get confirmText () {
    return this.stepIndex < this.steps.length - 1
      ? 'Next'
      : this.accountAsset
        ? 'Save'
        : 'Create'
  }

  get confirmAheadText () {
    return this.accountAsset ? 'Save' : 'Create'
  }

  get canPost () {
    return this.stepIndex === this.steps.length - 1
  }

  @Watch('value')
  onValueChanged (val: boolean) {
    if (val) {
      this.resetStep()
      this.initInput()
    }
    this.showDialog = val
  }

  resetStep () {
    this.stepIndex = this.step
  }

  initInput () {
    if (this.accountAsset) {
      const {
        name, notes, property
      } = this.accountAsset

      this.infoInput = {
        name,
        notes
      }
      if (!property) {
        return
      }

      const { address1, address2, city, state, country, zipCode, propertyType, propertyStatus, owner, price, creationTime, buyTime } = property
      const stateIdx = states.findIndex(stateItem => stateItem === state)

      this.propertyInput = {
        address1,
        address2,
        city,
        stateIdx,
        country,
        zipCode,
        propertyType,
        owner,
        price,
        propertyStatus,
        creationTime,
        buyTime
      }
    } else {
      this.clearForm()
    }
  }

  handleStepClick (index: number) {
    if (!this.accountAsset) {
      return
    }

    const pass = this.handleValidate()
    if (!pass) {
      return
    }

    this.stepIndex = index
  }

  handleValidate () {
    const { stepIndex, steps } = this
    const refName = steps[stepIndex][0] as string
    return (this.$refs[refName] as Vue & {
      validate: () => null | { [key: string]: number | string }
    }).validate()
  }

  handleConfirm (event: Event, finishAhead = false) {
    const pass = this.handleValidate()
    if (!pass) {
      return
    }

    if (this.stepIndex === this.steps.length - 1 || finishAhead) {
      this.loading = true
      if (this.accountAsset) {
        this.handleUpdate()
      } else {
        this.handleCreate()
      }
    } else {
      this.stepIndex += 1
    }
  }

  handleBack () {
    if (this.stepIndex > 0) {
      this.stepIndex -= 1
    } else {
      this.showDialog = false
    }
  }

  handleCreate () {
    return this.handleCreateProperty()
  }

  handleUpdate () {
    return this.handleUpdateProperty()
  }

  async handleCreateProperty () {
    const {
      infoInput: {
        name,
        notes
      },
      propertyInput: {
        address1,
        address2,
        city,
        stateIdx,
        country,
        zipCode,
        propertyType,
        owner,
        price,
        propertyStatus,
        creationTime,
        buyTime
      }
    } = this

    try {
      const res = await this.$apollo.mutate({
        mutation: newAssetProperty,
        variables: {
          input: {
            name,
            notes,
            address1,
            address2,
            city,
            state: states[stateIdx],
            country,
            zipCode,
            propertyType,
            owner,
            price,
            propertyStatus,
            creationTime,
            buyTime
          },
          accountId: this.accountId
        }
      })
      this.clearForm()
      this.onSuccess('Create the new property successfully', res.data.newAssetProperty.id)
    } catch (e) {
      this.loading = false
      showErrors(e.graphQLErrors)
    }
  }

  async handleUpdateProperty () {
    const {
      infoInput: {
        name,
        notes
      },
      propertyInput: {
        address1,
        address2,
        city,
        stateIdx,
        country,
        zipCode,
        propertyType,
        owner,
        price,
        propertyStatus,
        creationTime,
        buyTime
      }
    } = this

    try {
      const res = await this.$apollo.mutate({
        mutation: editAssetProperty,
        variables: {
          input: {
            name,
            notes,
            address1,
            address2,
            city,
            state: states[stateIdx],
            country,
            zipCode,
            propertyType,
            owner,
            price,
            propertyStatus,
            creationTime,
            buyTime
          },
          accountAssetId: +this.accountAsset.id
        }
      })
      this.clearForm()
      this.onSuccess('Update property successfully')
    } catch (e) {
      this.loading = false
      showErrors(e.graphQLErrors)
    }
  }

  clearForm () {
    this.loading = false
    this.infoInput = {
      name: '',
      notes: ''
    }
    this.propertyInput = {
      address1: '',
      address2: '',
      city: '',
      stateIdx: -1,
      country: '',
      zipCode: '',
      propertyType: '',
      owner: '',
      price: '',
      propertyStatus: '',
      creationTime: '',
      buyTime: ''
    }
  }

  @Emit('cancel')
  handleCancel () {
    this.$emit('input', false)
    this.clearForm()
  }

  @Emit('success')
  onSuccess (msg = '', id?: number) {
    this.$emit('input', false)
    this.resetStep()
    if (msg) {
      showNotification({
        msg,
        type: SnackType.Success
      })
    }
    return id
  }
}
